/**
 * Persistent credit balance pill for the portal top bar.
 * Polls every 30s. Opens a flyout with quick actions.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Coins, Plus, BarChart3, Zap } from 'lucide-react';
import { billingApi } from '../../services/billing';
import { formatMoney } from '../../lib/geo';
import { usePortalStore } from '../../stores';

export const CreditMeter = () => {
  const [wallet, setWallet] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const popRef = useRef(null);
  const isDemo = !!usePortalStore((s) => s.user?.is_demo);

  const load = async () => {
    try {
      const { data } = await billingApi.wallet();
      setWallet(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    const handler = () => load();
    window.addEventListener('credits:changed', handler);
    return () => { clearInterval(id); window.removeEventListener('credits:changed', handler); };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!wallet) return null;
  const isUnlimited = !!wallet.unlimited;
  const bal = wallet.effective_balance ?? wallet.credit_balance ?? 0;
  const color = isUnlimited ? '#A78BFA' : (bal > 100 ? '#10B981' : bal > 20 ? '#F59E0B' : '#EF4444');
  const level = isUnlimited ? 'unlimited' : (bal > 100 ? 'healthy' : bal > 20 ? 'low' : 'critical');

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all hover:opacity-90"
        style={{
          background: 'var(--layer-2)',
          border: '1px solid var(--hairline)',
          color: 'var(--nexus-text-primary)',
        }}
        data-testid="credit-meter"
        title={isUnlimited ? 'Unlimited credits — super-admin' : `${bal.toFixed(1)} credits · ${wallet.currency}`}
      >
        <Coins size={13} style={{ color }} />
        <span className="font-mono" data-testid="credit-balance">{isUnlimited ? '∞' : bal.toFixed(1)}</span>
        <span className="opacity-70">credits</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-72 rounded-xl overflow-hidden z-50"
            style={{
              background: 'var(--layer-1)',
              border: '1px solid var(--hairline)',
              boxShadow: '0 20px 40px -10px rgba(0,0,0,0.4)',
            }}
            data-testid="credit-meter-popover"
          >
            <div className="p-4 border-b" style={{ borderColor: 'var(--hairline)' }}>
              <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>
                {isUnlimited ? 'SUPER-ADMIN · UNLIMITED' : 'WALLET BALANCE'}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-heading text-3xl font-semibold text-nexus-text-primary">
                  {isUnlimited ? '∞' : bal.toFixed(1)}
                </span>
                <span className="text-[12px] text-nexus-text-tertiary">credits</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--layer-0)' }}>
                <div className="h-full transition-all" style={{
                  width: isUnlimited ? '100%' : `${Math.min(100, (bal / 200) * 100)}%`, background: color,
                }} />
              </div>
              <div className="mt-1.5 text-[11px]" style={{ color: color }}>
                {isUnlimited ? '✨ All models · all features unlocked'
                  : level === 'critical' ? '⚠️ Critical — top up now'
                  : level === 'low' ? 'Running low' : 'Healthy'}
              </div>
              {!isUnlimited && wallet.pool_balance > 0 && (
                <div className="mt-2 text-[10px] text-nexus-text-tertiary">
                  + {wallet.pool_balance.toFixed(1)} from org pool
                </div>
              )}
            </div>
            <div className="p-2 space-y-0.5">
              {!isDemo && (
                <button
                  onClick={() => { setOpen(false); navigate('/portal/pricing'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12.5px] text-left hover:bg-nexus-bg-tertiary transition-colors text-nexus-text-primary"
                  data-testid="meter-buy-credits"
                >
                  <Plus size={14} style={{ color: '#F26B1F' }} />
                  <span className="flex-1">Buy credits</span>
                </button>
              )}
              <button
                onClick={() => { setOpen(false); navigate('/portal/usage'); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12.5px] text-left hover:bg-nexus-bg-tertiary transition-colors text-nexus-text-primary"
                data-testid="meter-usage"
              >
                <BarChart3 size={14} style={{ color: '#6BA6F5' }} />
                <span className="flex-1">Usage history</span>
              </button>
              <button
                onClick={() => { setOpen(false); navigate('/portal/settings?tab=auto_topup'); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12.5px] text-left hover:bg-nexus-bg-tertiary transition-colors text-nexus-text-primary"
                data-testid="meter-auto-topup">
                <Zap size={14} style={{ color: wallet.auto_topup?.enabled ? '#10B981' : '#718096' }} />
                <span className="flex-1">Auto top-up</span>
                <span className="text-[10px] font-mono" style={{ color: wallet.auto_topup?.enabled ? '#10B981' : '#718096' }}>
                  {wallet.auto_topup?.enabled ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreditMeter;
